/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import { layerSelectors, uiTypes, useSetupDialog } from '@opengeoweb/store';
import { LocationSearch } from './LocationSearch';
import { MY_LOCATION } from './MyMapLocation';
import { useDrawOnMap } from './utils';

export interface LocationSearchConnectProps {
  mapId?: string;
}

export const LocationSearchConnect: React.FC<LocationSearchConnectProps> = ({
  mapId = '',
}: LocationSearchConnectProps) => {
  const dialogType: uiTypes.DialogType = `${uiTypes.DialogTypes.Search}-${mapId}`;
  const { isDialogOpen } = useSetupDialog(dialogType);
  const [updateMapPin] = useDrawOnMap(mapId);
  const [updateMyLocation] = useDrawOnMap(mapId);

  const mapLayers = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayersByMapId(store, mapId),
  );

  const myLocationLayer = mapLayers.find((layer) => {
    if (layer.geojson?.features[0]?.properties?.name) {
      if (layer.geojson.features[0].properties.name === MY_LOCATION) {
        return true;
      }
    }
    return false;
  });
  const isMyLocationSelected = !!myLocationLayer;

  return (
    <LocationSearch
      isMyLocationSelected={isMyLocationSelected}
      isOpen={isDialogOpen}
      mapId={mapId}
      onSelectLocation={updateMapPin}
      onUpdateMyLocation={updateMyLocation}
    />
  );
};
